/* The prompt index.

   It used to be a hand-kept list of thirty entries. It is now that list plus
   every prompt in every lesson, which is where the prompts of this portal
   actually live: each of the ten materials of a lesson carries the prompt that
   makes it, and a teacher who wants "the prompt for a hands-on exercise" should
   not have to remember which lesson they saw one in.

   Nothing is copied to get that. A lesson's prompt is read out of the lesson,
   so it cannot drift from what the lesson shows; the cost is that this one page
   fetches the lessons rather than only the index. That is the right trade here
   and nowhere else: this page *is* the database, it is opened deliberately, and
   the catalogue's sixty tiles still draw from the index alone.

   Which rows of the index are lessons is not a new field — a lesson's row
   carries the goal at the expected level and the gate insists on it, so an
   entry with a goal is a lesson.

   Two filters, both in the address, so that a link to "the prompts for checking"
   is a link somebody can send. The values in the address are ids and Czech tags
   written by hand, never translated labels: a link sent from the English page
   has to filter the Czech one.

   OWNED BY ITEM 4 OF PLAN.md. */

import { Link, useSearchParams } from "react-router-dom";
import { Blocks } from "../blocks";
import { ModelPrompt } from "../blocks/prompts";
import { useDocs, useIndex } from "../lib/hooks";
import { useI18n } from "../lib/i18n";
import { usePortal } from "../lib/portal";
import { KIT_KINDS, isAbsent } from "../lib/types";
import type { Block, Doc, Localized, PromptSet } from "../lib/types";

interface Tag { v: string; label: string }

/* One card. Either a hand-written entry with its blocks, or one material's
   prompt out of a lesson. */
interface Entry {
  key: string;
  title: Localized;
  lead?: Localized;
  from?: { to: string; label: string };
  subject?: string;
  kind?: string;
  year?: number;
  tags: Tag[];
  blocks?: Block[];
  prompt?: PromptSet;
}

export function Prompts({ doc }: { doc: Doc }) {
  const { t, ui, href } = useI18n();
  const { taxonomy } = usePortal();
  const [params, setParams] = useSearchParams();
  const tag = params.get("tag");
  const subject = params.get("subject");

  const { data: index } = useIndex();
  /* A row with a goal is a lesson. Fetched in one go, and the page renders the
     hand-written half meanwhile rather than showing nothing. */
  const lessonIds = (index ?? []).filter((e) => e.goal).map((e) => e.id);
  const { data: lessons } = useDocs(lessonIds);

  const written: Entry[] = (doc.parts ?? []).map((part, i) => ({
    key: `w${i}`,
    title: part.title ?? "",
    lead: part.lead,
    tags: (part.tags ?? []).map((v) => ({ v, label: v })),
    blocks: part.blocks,
  }));

  const fromLessons: Entry[] = [];
  for (const id of lessonIds) {
    const lesson = lessons?.[id];
    if (!lesson || lesson.type !== "lesson") continue;
    const meta = lesson.meta ?? {};
    const subjectName = meta.subject ? t(taxonomy?.subjects[meta.subject]?.name) : "";
    for (const item of lesson.kit) {
      if (isAbsent(item)) continue;
      fromLessons.push({
        key: `${id}:${item.kind}`,
        title: item.title,
        lead: item.claim,
        from: { to: href(`/m/${id}#${item.kind}`), label: t(lesson.title) },
        subject: meta.subject,
        kind: item.kind,
        year: meta.year,
        tags: [
          { v: item.kind, label: ui(`kit.${item.kind}`) },
          ...(meta.subject && subjectName ? [{ v: meta.subject, label: subjectName }] : []),
        ],
        prompt: item.prompt,
      });
    }
  }

  /* Written first — they are the method, and they are what somebody came for
     when they typed the address. Then the lessons, by subject, year and the
     order the ten materials are shown in. */
  const order = new Map(KIT_KINDS.map((k, i) => [k as string, i]));
  fromLessons.sort((a, b) =>
    (a.subject ?? "").localeCompare(b.subject ?? "", "cs")
    || (a.year ?? 0) - (b.year ?? 0)
    || (order.get(a.kind ?? "") ?? 0) - (order.get(b.kind ?? "") ?? 0));

  const all = [...written, ...fromLessons];
  const onTag = (e: Entry) => !tag || e.tags.some((x) => x.v === tag);
  const onSubject = (e: Entry) => !subject || e.subject === subject;
  const shown = all.filter((e) => onTag(e) && onSubject(e));

  /* A chip row is drawn from what is there, and counted against the *other*
     filter: a chip that says three and then shows nothing is worse than no
     chip. Before the first lesson is written the subject row has nothing in it
     and does not appear, which is how the page looked before and how it should
     look until it has something to say. */
  const kinds = axis(all.filter(onSubject), (e) => e.tags.filter((x) => x.v !== e.subject));
  const subjects = axis(all.filter(onTag), (e) => e.tags.filter((x) => x.v === e.subject));

  function pick(next: Record<string, string | null>) {
    const now: Record<string, string> = {};
    if (tag) now.tag = tag;
    if (subject) now.subject = subject;
    for (const [k, v] of Object.entries(next)) {
      if (v === null) delete now[k]; else now[k] = v;
    }
    setParams(now, { replace: true });
  }

  return (
    <>
      <Chips label={ui("prompts.what")} axis={kinds} active={tag}
             total={all.filter(onSubject).length}
             allLabel={ui("prompts.all")}
             onPick={(v) => pick({ tag: v })} />
      {subjects.length ? (
        <Chips label={ui("prompts.subject")} axis={subjects} active={subject}
               onPick={(v) => pick({ subject: v })} />
      ) : null}

      {shown.length ? null : <p className="cat-empty">{ui("prompts.none")}</p>}

      <div className="prompt-list">
        {shown.map((e) => (
          <article key={e.key} className="prompt-card">
            <h3>{t(e.title)}</h3>
            {e.lead ? <p className="lead">{t(e.lead)}</p> : null}

            {e.blocks ? <Blocks blocks={e.blocks} /> : null}
            {e.prompt ? <ModelPrompt prompt={e.prompt} /> : null}

            {e.from ? (
              <p className="prompt-from">
                <Link to={e.from.to}>{ui("prompts.open")} {e.from.label} →</Link>
              </p>
            ) : null}

            {e.tags.length ? (
              <div className="prompt-tags">
                {e.tags.map((x) => (
                  <button key={x.v} type="button" className="prompt-tag"
                          onClick={() => pick(x.v === e.subject
                            ? { subject: x.v } : { tag: x.v })}>
                    {x.label}
                  </button>
                ))}
              </div>
            ) : null}
          </article>
        ))}
      </div>
    </>
  );
}

/* One axis of the filter: every value on it, with how many cards carry it. */
function axis(all: Entry[], of: (e: Entry) => Tag[]): (Tag & { n: number })[] {
  const seen = new Map<string, Tag & { n: number }>();
  for (const entry of all) {
    for (const t of of(entry)) {
      const row = seen.get(t.v);
      if (row) row.n += 1; else seen.set(t.v, { ...t, n: 1 });
    }
  }
  return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label, "cs"));
}

function Chips({ label, axis, active, total, allLabel, onPick }: {
  label: string; axis: (Tag & { n: number })[]; active: string | null;
  total?: number; allLabel?: string; onPick: (v: string | null) => void;
}) {
  if (!axis.length) return null;
  return (
    <div className="chips">
      <span className="chips-label">{label}</span>
      {allLabel ? (
        <button type="button" className={"chip" + (active ? "" : " on")}
                onClick={() => onPick(null)}>
          {allLabel} <span className="chip-n">{total}</span>
        </button>
      ) : null}
      {axis.map((x) => (
        <button key={x.v} type="button" className={"chip" + (active === x.v ? " on" : "")}
                onClick={() => onPick(active === x.v ? null : x.v)}>
          {x.label} <span className="chip-n">{x.n}</span>
        </button>
      ))}
    </div>
  );
}
